import { LOGOUT_PATH } from '../auth/logoutPaths';
import { markSchedulerLoggedOut } from '../auth/schedulerSession';
import { setTransactionServerAuthToken } from '../api/schedulingApis';
import { clearAllDashboardCaches } from '../dashboard/dashboardProjectCache';
import {
  invalidateRequestTrackingCache,
  setRequestTrackingAuthToken,
} from '../features/request-tracking';
import { resetTaskStore } from '../stores';
import { clearDatabase } from '../stores/db';
import { getMainPortalBaseUrl } from './config';
import {
  clearPersistedPortalSession,
  getPortalReturnUrl,
  peekPortalReturnUrl,
} from './portalSession';

export { LOGOUT_PATH, isLogoutPath } from '../auth/logoutPaths';

export const LOGOUT_RETURN_URL_KEY = 'gantt-planner:logoutReturnUrl';

/**
 * Best portal destination after logout / session expiry:
 * portal returnUrl (session) → cookie returnUrl → saved logout URL → Portal2 base (menu).
 */
export function resolvePortalRedirectUrl(): string {
  const candidates = [
    getPortalReturnUrl(),
    peekPortalReturnUrl(),
    getLogoutReturnUrl(),
    getMainPortalBaseUrl(),
  ];
  for (const raw of candidates) {
    const url = String(raw ?? '').trim();
    if (url) {
      return url;
    }
  }
  return '';
}

export function performLogoutSync(): { returnUrl: string } {
  const returnUrl = resolvePortalRedirectUrl();

  if (returnUrl && typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(LOGOUT_RETURN_URL_KEY, returnUrl);
    } catch {
      // ignore
    }
  }

  markSchedulerLoggedOut();
  clearPersistedPortalSession();
  setTransactionServerAuthToken(null);
  setRequestTrackingAuthToken(null);
  resetTaskStore();
  invalidateRequestTrackingCache();
  clearAllDashboardCaches();

  return { returnUrl };
}

export function clearLocalDataInBackground(): void {
  void clearDatabase().catch((err) => {
    console.warn('[Logout] Failed to clear IndexedDB:', err);
  });
}

export async function performLogout(): Promise<{ returnUrl: string }> {
  const result = performLogoutSync();
  try {
    await clearDatabase();
  } catch (err) {
    console.warn('[Logout] Failed to clear IndexedDB:', err);
  }
  return result;
}

export function getLogoutReturnUrl(): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }
  try {
    return sessionStorage.getItem(LOGOUT_RETURN_URL_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

export function clearLogoutReturnUrl(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(LOGOUT_RETURN_URL_KEY);
  } catch {
    // ignore
  }
}

export function redirectToPortal(returnUrl?: string): void {
  const url = (returnUrl ?? resolvePortalRedirectUrl()).trim();
  if (!url || typeof window === 'undefined') {
    return;
  }
  clearLogoutReturnUrl();
  window.location.assign(url);
}
